import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Receipt, Plus, Download, Eye, XCircle, Filter, Search, Trash2, ChevronDown, X, Mail, FileCheck, AlertTriangle, FileWarning, Play, RefreshCw, Check, Clock, Calendar, ChevronLeft, ChevronRight, Pencil, Copy, Repeat, Pause, Power, Zap } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import { rechnungApi } from '../api/rechnungApi';
import { wiederkehrendeRechnungApi } from '../api/wiederkehrendeRechnungApi';
import { getAllKunden } from '../api/kundenApi';
import apiClient from '../api/apiClient';
import { useRechnungenRefresh } from '../hooks/useDataRefresh';

function Rechnungen() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rechnungen');
  const [rechnungen, setRechnungen] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mahnwesen State
  const [mahnwesenStats, setMahnwesenStats] = useState(null);
  const [mahnwesenSettings, setMahnwesenSettings] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [testingMahnlauf, setTestingMahnlauf] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Daueraufträge State
  const [dauerauftraege, setDauerauftraege] = useState([]);
  const [dauerauftraegeLoading, setDauerauftraegeLoading] = useState(false);
  const [triggeringId, setTriggeringId] = useState(null);

  // Filter
  const [filterKunde, setFilterKunde] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterJahr, setFilterJahr] = useState(new Date().getFullYear().toString());
  const [filterRechnungsnummer, setFilterRechnungsnummer] = useState('');

  // Kundensuche State
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const kundenDropdownRef = useRef(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Initial load
  useEffect(() => {
    loadData();
  }, []);

  // Filter-Wechsel - silent reload um Flackern zu vermeiden
  useEffect(() => {
    if (rechnungen.length > 0 || kunden.length > 0) {
      loadData(true);
    }
  }, [filterKunde, filterStatus, filterJahr, filterRechnungsnummer]);

  useEffect(() => {
    if (activeTab === 'mahnwesen') {
      loadMahnwesenData();
    } else if (activeTab === 'dauerauftraege') {
      loadDauerauftraege();
    }
  }, [activeTab]);

  const loadDauerauftraege = async () => {
    setDauerauftraegeLoading(true);
    try {
      const data = await wiederkehrendeRechnungApi.getAll();
      setDauerauftraege(data);
    } catch (error) {
      console.error('Fehler beim Laden der Daueraufträge:', error);
    } finally {
      setDauerauftraegeLoading(false);
    }
  };

  const handleToggleDauerauftrag = async (id, aktiv) => {
    try {
      await wiederkehrendeRechnungApi.update(id, { aktiv: !aktiv });
      setDauerauftraege(prev => prev.map(d => d.id === id ? { ...d, aktiv: !aktiv } : d));
    } catch (error) {
      console.error('Fehler beim Umschalten:', error);
    }
  };

  const handleTriggerDauerauftrag = async (id) => {
    setTriggeringId(id);
    try {
      const result = await wiederkehrendeRechnungApi.trigger(id);
      alert(`Rechnung ${result.rechnung?.rechnungsnummer || ''} erstellt!`);
      loadDauerauftraege();
      loadData(true); // Rechnungsliste aktualisieren
    } catch (error) {
      alert('Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setTriggeringId(null);
    }
  };

  const handleDeleteDauerauftrag = async (id, name) => {
    if (!confirm(`Dauerauftrag "${name}" wirklich löschen?`)) return;
    try {
      await wiederkehrendeRechnungApi.delete(id);
      setDauerauftraege(prev => prev.filter(d => d.id !== id));
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
    }
  };

  const loadMahnwesenData = async () => {
    try {
      const [statsRes, settingsRes, statusRes] = await Promise.all([
        apiClient.get('/mahnungen/statistiken'),
        apiClient.get('/mahnungen/einstellungen'),
        apiClient.get('/mahnungen/status')
      ]);
      setMahnwesenStats(statsRes.data);
      setMahnwesenSettings(settingsRes.data);
      setCronStatus(statusRes.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mahnwesen-Daten:', error);
    }
  };

  const handleTestMahnlauf = async () => {
    setTestingMahnlauf(true);
    setTestResult(null);
    try {
      const response = await apiClient.post('/mahnungen/mahnlauf');
      setTestResult({
        success: true,
        message: `${response.data.versendeteAktuelleMahnungen} Mahnungen versendet, ${response.data.aktualisiertStatus} Status aktualisiert`
      });
      // Statistiken neu laden
      const statsRes = await apiClient.get('/mahnungen/statistiken');
      setMahnwesenStats(statsRes.data);
      // Auch Rechnungen neu laden
      loadData();
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.error || err.message
      });
    } finally {
      setTestingMahnlauf(false);
    }
  };

  const getWochentagName = (tag) => {
    const tage = { 0: 'Sonntag', 1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch', 4: 'Donnerstag', 5: 'Freitag', 6: 'Samstag' };
    return tage[tag] || 'Montag';
  };

  // Schließe Dropdown wenn außerhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(event.target)) {
        setShowKundenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const params = {};
      if (filterKunde) params.kundeId = filterKunde;
      if (filterStatus) params.status = filterStatus;
      if (filterJahr) params.jahr = filterJahr;
      if (filterRechnungsnummer) params.rechnungsnummer = filterRechnungsnummer;

      const [rechnungenData, kundenData] = await Promise.all([
        rechnungApi.getRechnungen(params),
        getAllKunden()
      ]);

      setRechnungen(rechnungenData);
      setKunden(kundenData);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Echtzeit-Updates: Reagiere auf KI-Assistent Aktionen
  const handleRechnungenRefresh = useCallback(() => {
    loadData(true); // Silent reload ohne Loading-Spinner
  }, [filterKunde, filterStatus, filterJahr, filterRechnungsnummer]);

  useRechnungenRefresh(handleRechnungenRefresh);

  const handleDuplicate = async (rechnung) => {
    try {
      const fullRechnung = await rechnungApi.getRechnung(rechnung.id);
      const duplicateData = {
        kundeId: fullRechnung.kundeId,
        anfrageId: fullRechnung.anfrageId || '',
        kvaId: fullRechnung.kvaId || '',
        einleitung: fullRechnung.einleitung || '',
        schlusstext: fullRechnung.schlusstext || '',
        betreff: fullRechnung.betreff || '',
        kundenReferenz: fullRechnung.kundenReferenz || '',
        gesamtRabattProzent: fullRechnung.gesamtRabattProzent || 0,
        leistungsdatum: fullRechnung.leistungsdatum || '',
        skontoProzent: fullRechnung.skontoProzent || '',
        skontoTage: fullRechnung.skontoTage || '',
        positionen: (fullRechnung.positionen || []).map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis / 100, // Cent → Euro
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          berechnungsSchritte: pos.berechnungsSchritte
            ? pos.berechnungsSchritte.map(s => ({
                ...s,
                einzelpreis: s.einzelpreis / 100,
                gesamt: s.gesamt / 100
              }))
            : []
        }))
      };
      navigate('/rechnungen/neu', { state: { fromDuplicate: duplicateData } });
    } catch (error) {
      console.error('Fehler beim Duplizieren:', error);
      alert('Fehler beim Duplizieren der Rechnung');
    }
  };

  const handleStorno = async (rechnung) => {
    const kundeEmail = rechnung.kunde?.email;
    const confirmMsg = kundeEmail
      ? `Möchten Sie die Rechnung ${rechnung.rechnungsnummer} wirklich stornieren?\n\nDie Stornorechnung wird automatisch an ${kundeEmail} gesendet.`
      : `Möchten Sie die Rechnung ${rechnung.rechnungsnummer} wirklich stornieren?`;

    if (!confirm(confirmMsg)) {
      return;
    }

    try {
      const result = await rechnungApi.createStornorechnung(rechnung.id);
      alert(result.message || 'Rechnung wurde storniert');
      loadData();
    } catch (error) {
      console.error('Fehler beim Stornieren:', error);
      alert(error.response?.data?.error || 'Fehler beim Stornieren der Rechnung');
    }
  };

  const handleDelete = async (rechnung) => {
    if (!confirm(`Möchten Sie den Entwurf ${rechnung.rechnungsnummer} wirklich löschen?`)) {
      return;
    }

    try {
      await rechnungApi.deleteRechnung(rechnung.id);
      loadData();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen der Rechnung');
    }
  };

  const handleStatusChange = async (rechnung, newStatus) => {
    // Optimistisches Update - sofort im UI aktualisieren
    const previousStatus = rechnung.status;
    setRechnungen(prev => prev.map(r =>
      r.id === rechnung.id ? { ...r, status: newStatus } : r
    ));

    try {
      await rechnungApi.updateRechnung(rechnung.id, { status: newStatus });
      // Kein loadData() nötig - State ist bereits aktualisiert
    } catch (error) {
      console.error('Fehler beim Status-Update:', error);
      // Bei Fehler: Status zurücksetzen
      setRechnungen(prev => prev.map(r =>
        r.id === rechnung.id ? { ...r, status: previousStatus } : r
      ));
      alert(error.response?.data?.error || 'Fehler beim Aktualisieren des Status');
    }
  };

  const handleSendEmail = async (rechnung) => {
    if (!rechnung.kunde.email) {
      alert('Kunde hat keine E-Mail-Adresse hinterlegt');
      return;
    }

    if (!confirm(`Rechnung ${rechnung.rechnungsnummer} an ${rechnung.kunde.email} senden?`)) {
      return;
    }

    try {
      const result = await rechnungApi.sendRechnung(rechnung.id);
      alert(result.message || 'E-Mail wurde erfolgreich gesendet');
      loadData();
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      alert('Fehler beim Senden: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusBadge = (status, anzahlMahnungen = 0) => {
    const badges = {
      'entwurf': 'bg-gray-100 text-gray-700',
      'versendet': 'bg-blue-100 text-blue-700',
      'bezahlt': 'bg-green-100 text-green-700',
      'ueberfaellig': 'bg-orange-100 text-orange-700',
      'storniert': 'bg-red-100 text-red-700'
    };

    const labels = {
      'entwurf': 'Entwurf',
      'versendet': 'Versendet',
      'bezahlt': 'Bezahlt',
      'ueberfaellig': 'Überfällig',
      'storniert': 'Storniert'
    };

    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
          {labels[status]}
        </span>
        {anzahlMahnungen > 0 && status !== 'bezahlt' && status !== 'storniert' && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium" title={`${anzahlMahnungen} Mahnung(en) versendet`}>
            <AlertTriangle className="w-3 h-3" />
            {anzahlMahnungen}x
          </span>
        )}
      </div>
    );
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Jahre für Filter generieren (2023 bis aktuelles Jahr + 1)
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = 2023; y <= currentYear + 1; y++) {
    years.push(y);
  }

  // Gefilterte Kundenliste basierend auf Suche
  const gefilterteKunden = kunden.filter(kunde => {
    const suchText = kundenSuche.toLowerCase();
    return (
      kunde.name.toLowerCase().includes(suchText) ||
      kunde.email?.toLowerCase().includes(suchText) ||
      kunde.telefon?.toLowerCase().includes(suchText)
    );
  });

  // Kunde auswählen
  const handleKundeSelect = (kunde) => {
    setSelectedKunde(kunde);
    setFilterKunde(kunde.id);
    setKundenSuche('');
    setShowKundenDropdown(false);
  };

  // Kundenauswahl entfernen
  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setFilterKunde('');
    setKundenSuche('');
  };

  // Prüfen ob Filter aktiv sind
  const hasActiveFilters = filterKunde || filterStatus || filterRechnungsnummer || (filterJahr && filterJahr !== new Date().getFullYear().toString());

  // Alle Filter zurücksetzen
  const resetFilters = () => {
    setFilterKunde('');
    setFilterStatus('');
    setFilterJahr(new Date().getFullYear().toString());
    setFilterRechnungsnummer('');
    setSelectedKunde(null);
    setKundenSuche('');
    setCurrentPage(1);
  };

  // Pagination Berechnung
  const totalPages = Math.ceil(rechnungen.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRechnungen = rechnungen.slice(startIndex, endIndex);

  // Reset auf Seite 1 wenn Filter sich ändern
  useEffect(() => {
    setCurrentPage(1);
  }, [filterKunde, filterStatus, filterJahr, filterRechnungsnummer]);

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold flex items-center gap-3">
              <Receipt className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
              Rechnungen
            </h1>
            <p className="text-sm text-text-muted mt-1 sm:hidden">
              {rechnungen.length} Rechnungen
            </p>
            {/* Mobile: Button unter Titel */}
            {activeTab === 'rechnungen' && (
              <button
                onClick={() => navigate('/rechnungen/neu')}
                className="flex sm:hidden items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition mt-3"
              >
                <Plus className="w-5 h-5" />
                Neue Rechnung
              </button>
            )}
          </div>

          {/* Desktop: Button rechts */}
          {activeTab === 'rechnungen' && (
            <button
              onClick={() => navigate('/rechnungen/neu')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition"
            >
              <Plus className="w-5 h-5" />
              Neue Rechnung
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('rechnungen')}
            className={`px-4 py-3 font-medium text-sm transition border-b-2 -mb-px ${
              activeTab === 'rechnungen'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Rechnungen
              <span className="px-2 py-0.5 text-xs bg-gray-100 rounded-full">{rechnungen.length}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('mahnwesen')}
            className={`px-4 py-3 font-medium text-sm transition border-b-2 -mb-px ${
              activeTab === 'mahnwesen'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileWarning className="w-4 h-4" />
              Mahnwesen
              {mahnwesenStats?.ueberfaelligeRechnungen > 0 && (
                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                  {mahnwesenStats.ueberfaelligeRechnungen}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('dauerauftraege')}
            className={`px-4 py-3 font-medium text-sm transition border-b-2 -mb-px ${
              activeTab === 'dauerauftraege'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Repeat className="w-4 h-4" />
              Daueraufträge
              {dauerauftraege.filter(d => d.aktiv).length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {dauerauftraege.filter(d => d.aktiv).length}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Rechnungen Tab */}
        {activeTab === 'rechnungen' && (
          <>
            <p className="text-text-muted mb-4">
              {rechnungen.length} Rechnungen gefunden
            </p>

        {/* Filter */}
        <div className="bg-surface rounded-card shadow-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-text-muted" />
              <h2 className="text-lg font-semibold">Filter</h2>
            </div>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-primary hover:text-primary-dark font-medium text-sm transition"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Rechnungsnummer Suche */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Rechnungsnummer
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filterRechnungsnummer}
                  onChange={(e) => setFilterRechnungsnummer(e.target.value)}
                  placeholder="z.B. RE-2025-00001"
                  className="w-full pl-10 pr-3 py-2 border border-border rounded-button bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Kunde Filter mit Suchfunktion */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Kunde
              </label>
              <div className="relative" ref={kundenDropdownRef}>
                {/* Ausgewählter Kunde oder Suchfeld */}
                {selectedKunde ? (
                  <div className="flex items-center justify-between w-full px-3 py-2 border border-border rounded-button bg-gray-50">
                    <span className="font-medium truncate">{selectedKunde.name}</span>
                    <button
                      type="button"
                      onClick={handleKundeRemove}
                      className="p-1 hover:bg-gray-200 rounded transition ml-2"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={kundenSuche}
                      onChange={(e) => {
                        setKundenSuche(e.target.value);
                        setShowKundenDropdown(true);
                      }}
                      onFocus={() => setShowKundenDropdown(true)}
                      placeholder="Kunde suchen..."
                      className="w-full pl-10 pr-10 py-2 border border-border rounded-button bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                )}

                {/* Dropdown Liste */}
                {showKundenDropdown && !selectedKunde && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {gefilterteKunden.length === 0 ? (
                      <div className="px-4 py-3 text-text-muted text-center">
                        {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                      </div>
                    ) : (
                      gefilterteKunden.map(kunde => (
                        <button
                          key={kunde.id}
                          type="button"
                          onClick={() => handleKundeSelect(kunde)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                        >
                          <div className="font-medium">{kunde.name}</div>
                          <div className="text-sm text-text-muted flex gap-3">
                            {kunde.email && <span>{kunde.email}</span>}
                            {kunde.telefon && <span>{kunde.telefon}</span>}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-button bg-surface"
              >
                <option value="">Alle Status</option>
                <option value="entwurf">Entwurf</option>
                <option value="versendet">Versendet</option>
                <option value="bezahlt">Bezahlt</option>
                <option value="ueberfaellig">Überfällig</option>
                <option value="storniert">Storniert</option>
                <option value="stornorechnung">Stornorechnung</option>
              </select>
            </div>

            {/* Jahr Filter */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Jahr
              </label>
              <select
                value={filterJahr}
                onChange={(e) => setFilterJahr(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-button bg-surface"
              >
                <option value="">Alle Jahre</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Summen Übersicht */}
        {rechnungen.length > 0 && (
          <div className="bg-surface rounded-card shadow-card p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Übersicht</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-text-muted">Entwürfe</p>
                <p className="text-xl font-bold">
                  {formatCurrency(
                    rechnungen
                      .filter(r => r.status === 'entwurf' && !r.storniert)
                      .reduce((sum, r) => sum + r.bruttogesamt, 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Offen</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(
                    rechnungen
                      .filter(r => (r.status === 'versendet' || r.status === 'ueberfaellig') && !r.storniert)
                      .reduce((sum, r) => sum + r.bruttogesamt, 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Bezahlt</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(
                    rechnungen
                      .filter(r => r.status === 'bezahlt' && !r.storniert)
                      .reduce((sum, r) => sum + r.bruttogesamt, 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Storniert</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(
                    rechnungen
                      .filter(r => r.status === 'storniert' && !r.storniert)
                      .reduce((sum, r) => sum + Math.abs(r.bruttogesamt), 0)
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rechnungen Liste */}
        <div className="bg-surface rounded-card shadow-card overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : rechnungen.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-text-muted">Keine Rechnungen gefunden</p>
              <button
                onClick={() => navigate('/rechnungen/neu')}
                className="mt-4 text-primary hover:underline"
              >
                Erste Rechnung erstellen
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Rechnungsnr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Datum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Fällig
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                      Betrag
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedRechnungen.map(rechnung => (
                    <tr key={rechnung.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium">
                          {rechnung.rechnungsnummer}
                        </div>
                        {rechnung.storniert && (
                          <div className="text-xs text-danger flex items-center gap-1 mt-1">
                            <XCircle className="w-3 h-3" />
                            Stornorechnung
                          </div>
                        )}
                        {rechnung.eRechnungFormat && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title="ZUGFeRD 2.1 E-Rechnung - XML in PDF eingebettet">
                              <FileCheck className="w-3 h-3" />
                              E-Rechnung
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {rechnung.kunde ? (
                          <Link
                            to={`/kunden/${rechnung.kunde.id}`}
                            className="text-primary hover:underline"
                          >
                            {rechnung.kunde.name}
                          </Link>
                        ) : (
                          <span className="text-text-muted">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-text-muted">
                        {formatDate(rechnung.datum)}
                      </td>
                      <td className="px-6 py-4 text-sm text-text-muted">
                        {formatDate(rechnung.faelligDatum)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium">
                        {formatCurrency(rechnung.bruttogesamt)}
                      </td>
                      <td className="px-6 py-4">
                        {rechnung.storniert ? (
                          // Stornorechnungen haben keinen bearbeitbaren Status
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Stornorechnung
                          </span>
                        ) : rechnung.status === 'entwurf' ? (
                          // Entwurf: Kann zu versendet, bezahlt oder überfällig geändert werden
                          <div className="flex items-center gap-2">
                            <select
                              value={rechnung.status}
                              onChange={(e) => handleStatusChange(rechnung, e.target.value)}
                              className="px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer bg-gray-100 text-gray-700"
                            >
                              <option value="entwurf">Entwurf</option>
                              <option value="versendet">Versendet</option>
                              <option value="bezahlt">Bezahlt</option>
                              <option value="ueberfaellig">Überfällig</option>
                            </select>
                          </div>
                        ) : rechnung.status === 'versendet' ? (
                          // Versendet: Kann zurück zu Entwurf, bezahlt oder überfällig geändert werden
                          <div className="flex items-center gap-2">
                            <select
                              value={rechnung.status}
                              onChange={(e) => handleStatusChange(rechnung, e.target.value)}
                              className="px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer bg-blue-100 text-blue-700"
                            >
                              <option value="versendet">Versendet</option>
                              <option value="entwurf">Entwurf</option>
                              <option value="bezahlt">Bezahlt</option>
                              <option value="ueberfaellig">Überfällig</option>
                            </select>
                            {rechnung.anzahlMahnungen > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium" title={`${rechnung.anzahlMahnungen} Mahnung(en) versendet`}>
                                <AlertTriangle className="w-3 h-3" />
                                {rechnung.anzahlMahnungen}x
                              </span>
                            )}
                          </div>
                        ) : rechnung.status === 'ueberfaellig' ? (
                          // Überfällig: Kann zu bezahlt oder zurück zu versendet geändert werden
                          <div className="flex items-center gap-2">
                            <select
                              value={rechnung.status}
                              onChange={(e) => handleStatusChange(rechnung, e.target.value)}
                              className="px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer bg-orange-100 text-orange-700"
                            >
                              <option value="ueberfaellig">Überfällig</option>
                              <option value="bezahlt">Bezahlt</option>
                              <option value="versendet">Versendet</option>
                            </select>
                            {rechnung.anzahlMahnungen > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium" title={`${rechnung.anzahlMahnungen} Mahnung(en) versendet`}>
                                <AlertTriangle className="w-3 h-3" />
                                {rechnung.anzahlMahnungen}x
                              </span>
                            )}
                          </div>
                        ) : rechnung.status === 'bezahlt' ? (
                          // Bezahlt: Kann zurück zu versendet oder überfällig geändert werden.
                          // Kein Mahnungs-Badge mehr anzeigen – eine bezahlte Rechnung ist nicht
                          // mehr im Mahnwesen (der rote Badge wirkte wie "weiterhin überfällig").
                          <div className="flex items-center gap-2">
                            <select
                              value={rechnung.status}
                              onChange={(e) => handleStatusChange(rechnung, e.target.value)}
                              className="px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer bg-green-100 text-green-700"
                            >
                              <option value="bezahlt">Bezahlt</option>
                              <option value="versendet">Versendet</option>
                              <option value="ueberfaellig">Überfällig</option>
                            </select>
                          </div>
                        ) : (
                          // storniert: Nur Badge anzeigen (keine Änderung möglich)
                          getStatusBadge(rechnung.status, rechnung.anzahlMahnungen)
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {rechnung.pdfPfad && (
                            <>
                              <a
                                href={rechnungApi.getPdfUrl(rechnung)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-primary hover:bg-primary-light rounded-button transition"
                                title="PDF öffnen"
                              >
                                <Eye className="w-4 h-4" />
                              </a>

                              <a
                                href={rechnungApi.getPdfUrl(rechnung)}
                                download={`Rechnung_${rechnung.rechnungsnummer}.pdf`}
                                className="p-2 text-text-muted hover:bg-gray-100 rounded-button transition"
                                title="PDF herunterladen"
                              >
                                <Download className="w-4 h-4" />
                              </a>

                              {/* E-Mail Senden Button - nur für Entwürfe mit Kunde-Email */}
                              {!rechnung.storniert && rechnung.status === 'entwurf' && rechnung.kunde.email && (
                                <button
                                  onClick={() => handleSendEmail(rechnung)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-button transition"
                                  title={`Per E-Mail an ${rechnung.kunde.email} senden (setzt Status auf "Versendet")`}
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}

                          {/* Duplizieren */}
                          {!rechnung.storniert && (
                            <button
                              onClick={() => handleDuplicate(rechnung)}
                              className="p-2 text-violet-600 hover:bg-violet-50 rounded-button transition"
                              title="Duplizieren"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          )}

                          {/* Bearbeiten - nur für Entwürfe */}
                          {rechnung.status === 'entwurf' && !rechnung.storniert && (
                            <button
                              onClick={() => navigate(`/rechnungen/${rechnung.id}/bearbeiten`)}
                              className="p-2 text-amber-600 hover:bg-amber-50 rounded-button transition"
                              title="Entwurf bearbeiten"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}

                          {rechnung.status === 'entwurf' && !rechnung.storniert && (
                            <button
                              onClick={() => handleDelete(rechnung)}
                              className="p-2 text-danger hover:bg-red-50 rounded-button transition"
                              title="Entwurf löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}

                          {rechnung.status !== 'storniert' && rechnung.status !== 'entwurf' && !rechnung.storniert && (
                            <button
                              onClick={() => handleStorno(rechnung)}
                              className="p-2 text-danger hover:bg-red-50 rounded-button transition"
                              title="Stornieren"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-text-muted">
                Zeige {startIndex + 1} - {Math.min(endIndex, rechnungen.length)} von {rechnungen.length} Rechnungen
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Zurück
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Zeige erste, letzte, aktuelle und benachbarte Seiten
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, idx, arr) => (
                      <span key={page} className="flex items-center">
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="px-2 text-text-muted">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 text-sm rounded-button transition ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'hover:bg-gray-50 border border-border'
                          }`}
                        >
                          {page}
                        </button>
                      </span>
                    ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

          </>
        )}

        {/* Mahnwesen Tab */}
        {activeTab === 'mahnwesen' && (
          <div className="space-y-6">
            {/* Statistiken */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface rounded-card shadow-card p-6 text-center">
                <p className="text-3xl font-bold text-primary">{mahnwesenStats?.offeneRechnungen || 0}</p>
                <p className="text-sm text-text-muted mt-1">Offene Rechnungen</p>
              </div>
              <div className="bg-surface rounded-card shadow-card p-6 text-center">
                <p className="text-3xl font-bold text-red-600">{mahnwesenStats?.ueberfaelligeRechnungen || 0}</p>
                <p className="text-sm text-text-muted mt-1">Überfällig</p>
              </div>
              <div className="bg-surface rounded-card shadow-card p-6 text-center">
                <p className="text-3xl font-bold text-orange-600">{mahnwesenStats?.mahnungenLetzter30Tage || 0}</p>
                <p className="text-sm text-text-muted mt-1">Mahnungen (30 Tage)</p>
              </div>
              <div className="bg-surface rounded-card shadow-card p-6 text-center">
                <p className="text-3xl font-bold text-gray-600">{mahnwesenStats?.gesamtMahnungen || 0}</p>
                <p className="text-sm text-text-muted mt-1">Mahnungen gesamt</p>
              </div>
            </div>

            {/* Einstellungen Uebersicht & Mahnlauf */}
            <div className="bg-surface rounded-card shadow-card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileWarning className="w-5 h-5 text-orange-600" />
                Mahnwesen-Einstellungen
              </h3>

              {mahnwesenSettings && (
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className={`w-3 h-3 rounded-full ${cronStatus?.laeuft ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <div className="flex-1">
                      <p className="font-medium">
                        {mahnwesenSettings.mahnlaufAktiv ? 'Automatischer Mahnlauf aktiv' : 'Automatischer Mahnlauf deaktiviert'}
                      </p>
                      {mahnwesenSettings.mahnlaufAktiv && cronStatus && (
                        <p className="text-sm text-text-muted flex items-center gap-2 mt-1">
                          <Clock className="w-4 h-4" />
                          {mahnwesenSettings.mahnlaufIntervall === 'woechentlich'
                            ? `${getWochentagName(mahnwesenSettings.mahnlaufWochentag)}s`
                            : 'Taeglich'
                          } um {mahnwesenSettings.mahnlaufUhrzeit} Uhr
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Mahnstufen */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium mb-3">Mahnstufen-Zeitplan</p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-2xl font-bold text-orange-500">{mahnwesenSettings.stufe1Tage}</p>
                        <p className="text-xs text-text-muted">Tage bis 1. Mahnung</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-2xl font-bold text-orange-600">{mahnwesenSettings.stufe2Tage}</p>
                        <p className="text-xs text-text-muted">Tage bis 2. Mahnung</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-2xl font-bold text-red-600">{mahnwesenSettings.stufe3Tage}</p>
                        <p className="text-xs text-text-muted">Tage bis 3. Mahnung</p>
                      </div>
                    </div>
                  </div>

                  {/* Manueller Mahnlauf */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Manueller Mahnlauf</p>
                        <p className="text-sm text-text-muted">Fuehrt den Mahnlauf sofort aus</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleTestMahnlauf}
                          disabled={testingMahnlauf}
                          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-100 transition disabled:opacity-50 text-sm"
                        >
                          {testingMahnlauf ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Laeuft...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4" />
                              Mahnlauf starten
                            </>
                          )}
                        </button>
                        {testResult && (
                          <span className={`flex items-center gap-1 text-sm ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                            {testResult.success ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                            {testResult.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Link zu Einstellungen */}
                  <div className="pt-4 border-t border-border">
                    <Link
                      to="/einstellungen"
                      className="text-primary hover:underline text-sm"
                    >
                      Alle Mahnwesen-Einstellungen bearbeiten →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Daueraufträge Tab */}
        {activeTab === 'dauerauftraege' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-text-muted">
                {dauerauftraege.length} Daueraufträge ({dauerauftraege.filter(d => d.aktiv).length} aktiv)
              </p>
              <button
                onClick={() => navigate('/rechnungen/dauerauftrag/neu')}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition"
              >
                <Plus className="w-4 h-4" />
                Neuer Dauerauftrag
              </button>
            </div>

            {dauerauftraegeLoading ? (
              <div className="text-center py-12 text-text-muted">Laden...</div>
            ) : dauerauftraege.length === 0 ? (
              <div className="bg-surface rounded-card shadow-card p-12 text-center">
                <Repeat className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Keine Daueraufträge</h3>
                <p className="text-text-muted mb-4">Erstellen Sie wiederkehrende Rechnungen, die automatisch generiert werden.</p>
                <button
                  onClick={() => navigate('/rechnungen/dauerauftrag/neu')}
                  className="px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition"
                >
                  Ersten Dauerauftrag erstellen
                </button>
              </div>
            ) : (
              <div className="bg-surface rounded-card shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 text-left text-sm text-text-muted">
                        <th className="px-4 py-3 font-medium">Name</th>
                        <th className="px-4 py-3 font-medium">Kunde</th>
                        <th className="px-4 py-3 font-medium">Intervall</th>
                        <th className="px-4 py-3 font-medium">Nächste Fälligkeit</th>
                        <th className="px-4 py-3 font-medium">Erstellt</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-right">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {dauerauftraege.map(da => {
                        const intervallLabel = {
                          MONATLICH: 'Monatlich',
                          QUARTALSWEISE: 'Quartalsweise',
                          HALBJAEHRLICH: 'Halbjährlich',
                          JAEHRLICH: 'Jährlich'
                        }[da.intervall] || da.intervall;

                        const naechste = new Date(da.naechsteFaelligkeit);
                        const istFaellig = naechste <= new Date();

                        return (
                          <tr key={da.id} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-3">
                              <Link to={`/rechnungen/dauerauftrag/${da.id}`} className="font-medium text-primary hover:underline">
                                {da.name}
                              </Link>
                              {da.betreff && (
                                <p className="text-xs text-text-muted mt-0.5">{da.betreff}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">{da.kunde?.name || '–'}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                                {intervallLabel}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={istFaellig ? 'text-red-600 font-medium' : ''}>
                                {naechste.toLocaleDateString('de-DE')}
                              </span>
                              {istFaellig && <span className="ml-1 text-xs text-red-500">(fällig)</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-text-muted">
                              {da.anzahlErstellt}x erstellt
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                da.aktiv ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                              }`}>
                                {da.aktiv ? 'Aktiv' : 'Pausiert'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleTriggerDauerauftrag(da.id)}
                                  disabled={triggeringId === da.id}
                                  className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded transition"
                                  title="Jetzt Rechnung erstellen"
                                >
                                  {triggeringId === da.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Zap className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleToggleDauerauftrag(da.id, da.aktiv)}
                                  className={`p-1.5 rounded transition ${
                                    da.aktiv ? 'text-text-muted hover:text-orange-600 hover:bg-orange-50' : 'text-text-muted hover:text-green-600 hover:bg-green-50'
                                  }`}
                                  title={da.aktiv ? 'Pausieren' : 'Aktivieren'}
                                >
                                  {da.aktiv ? <Pause className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => navigate(`/rechnungen/dauerauftrag/${da.id}`)}
                                  className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded transition"
                                  title="Bearbeiten"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDauerauftrag(da.id, da.name)}
                                  className="p-1.5 text-text-muted hover:text-red-600 hover:bg-red-50 rounded transition"
                                  title="Löschen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Rechnungen;
